import type { ApiErrorResponse } from "@shop-ledger/shared-types";
import { PLATFORM_API_BASE_URL } from "../env";

/**
 * Thin authenticated-fetch wrapper for `realPlatformApiClient.ts`, deliberately mirroring
 * `dashboard-admin/src/lib/apiClient.ts`'s `ApiError`/`apiRequest` shape (same class name,
 * same error-body parsing, same options shape) rather than inventing a second convention —
 * this app just has its own copy since `apiClient.ts` isn't published from a shared package
 * and this app's base URL derivation differs (see `env.ts`'s `PLATFORM_API_BASE_URL` doc
 * comment).
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function parseErrorDetail(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as ApiErrorResponse;
    if (typeof body.detail === "string") return body.detail;
    if (Array.isArray(body.detail)) return body.detail.map((item) => `${item.loc.join(".")}: ${item.msg}`).join("; ");
  } catch {
    // Response body wasn't JSON — fall through to the generic message below.
  }
  return `Request failed with status ${response.status}`;
}

export interface PlatformApiRequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  accessToken?: string | null;
}

function buildUrl(path: string, query?: PlatformApiRequestOptions["query"]): string {
  const url = new URL(`${PLATFORM_API_BASE_URL}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/** Every `/api/v1/platform/*` route besides `auth/{login,mfa/verify,refresh,logout}`
 * requires `Authorization: Bearer <aud:"platform" access token>` — pass `accessToken` for
 * those; the four auth routes are called without one (see `platform.py`'s router doc
 * comment: they can't require the very credential they're issuing/rotating). */
export async function platformApiRequest<T>(path: string, options: PlatformApiRequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, accessToken } = options;

  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    throw new ApiError(`Could not reach the backend: ${message}`, 0);
  }

  if (!response.ok) throw new ApiError(await parseErrorDetail(response), response.status);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
